// src/components/blog/BlogNewsletterForm.tsx
//
// Shared client-side newsletter form for the blog. Two visual variants:
//   - 'sidebar' (BlogSidebar.tsx widget — narrow, stacked)
//   - 'cta'     (BlogListingTemplate.tsx footer — wide, side-by-side)
//
// Hits POST /api/waitlist with source='blog-newsletter'. The route auto-syncs
// the signup into LeadBot's "Blog Newsletter Subscribers" list as a contact.

'use client';

import { useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

type Variant = 'sidebar' | 'cta';
type Status = 'idle' | 'loading' | 'success' | 'already' | 'error';

interface BlogNewsletterFormProps {
  variant: Variant;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function BlogNewsletterForm({ variant }: BlogNewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = email.trim();
    if (!trimmed) {
      setStatus('error');
      setErrorMessage('Please enter your email address.');
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, source: 'blog-newsletter' }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Subscription failed. Please try again.');
      }

      setStatus(data.alreadyRegistered ? 'already' : 'success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Subscription failed. Please try again.'
      );
    }
  };

  // ============ SUCCESS STATE (shared shape across variants) ============
  if (status === 'success' || status === 'already') {
    return (
      <div
        className={
          variant === 'sidebar'
            ? 'flex items-start gap-2 text-sm text-green-700'
            : 'flex items-center justify-center gap-2 text-base text-green-700'
        }
        role="status"
      >
        <CheckCircle2
          className={variant === 'sidebar' ? 'w-4 h-4 mt-0.5 flex-shrink-0' : 'w-5 h-5 flex-shrink-0'}
        />
        <span>
          {status === 'already'
            ? "You're already subscribed — thanks for your patience."
            : "You're in. Watch your inbox every Tuesday."}
        </span>
      </div>
    );
  }

  // ============ SIDEBAR VARIANT ============
  if (variant === 'sidebar') {
    return (
      <>
        <form className="space-y-3" onSubmit={handleSubmit} noValidate>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === 'error') setStatus('idle');
            }}
            disabled={status === 'loading'}
            aria-invalid={status === 'error'}
            aria-describedby={status === 'error' ? 'newsletter-sidebar-error' : undefined}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm disabled:opacity-60 disabled:bg-gray-50"
          />
          <button
            type="submit"
            disabled={status === 'loading' || !email.trim()}
            className="w-full px-4 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Subscribing...
              </>
            ) : (
              'Subscribe'
            )}
          </button>
        </form>
        {status === 'error' && (
          <p
            id="newsletter-sidebar-error"
            className="mt-2 flex items-start gap-1.5 text-xs text-red-600"
            role="alert"
          >
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{errorMessage}</span>
          </p>
        )}
      </>
    );
  }

  // ============ CTA VARIANT ============
  return (
    <>
      <form
        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        onSubmit={handleSubmit}
        noValidate
      >
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === 'error') setStatus('idle');
          }}
          disabled={status === 'loading'}
          aria-invalid={status === 'error'}
          aria-describedby={status === 'error' ? 'newsletter-cta-error' : undefined}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60 disabled:bg-gray-50"
        />
        <button
          type="submit"
          disabled={status === 'loading' || !email.trim()}
          className="px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'loading' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Subscribing...
            </>
          ) : (
            'Subscribe'
          )}
        </button>
      </form>
      {status === 'error' && (
        <p
          id="newsletter-cta-error"
          className="mt-3 flex items-center justify-center gap-1.5 text-sm text-red-600"
          role="alert"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMessage}</span>
        </p>
      )}
    </>
  );
}

export default BlogNewsletterForm;
